import { SkyDialog, SkyMessageBar, SkyTextarea } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { InstanceAdminView } from "@shared/types/instancesAdmin";
import { useMcpEnabled } from "@/hooks/use-mcp";
import { ErrorState } from "./ScreenState";

/**
 * Disable an instance (FR-20, canvas 12).
 *
 * The reason is a real field here, unlike on the customer screen, because
 * `POST /platform/instances/:id/disable` takes one and writes it to the audit
 * row's metadata. A field whose value the route would discard would be a lie
 * told politely, so where there is no route for it there is no field.
 *
 * Disabling is not deleting: the instance's schema in its tenant's database,
 * its runs and its audit trail stay exactly where they are. What stops is new
 * work.
 */
export function DisableInstanceDialog({
  open,
  onOpenChange,
  instance,
  onConfirm,
  isSaving,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: InstanceAdminView | null;
  onConfirm: (reason: string) => void;
  isSaving: boolean;
  error: unknown;
}) {
  // Empty on every opening: the caller remounts it with a key, so a reason
  // typed for one instance cannot be recorded against the next.
  const [reason, setReason] = useState("");
  const mcpEnabled = useMcpEnabled();

  if (!instance) return null;

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Disable ${instance.name}`}
      description="New runs are refused and every schedule in it is switched off. Nothing is deleted."
      size="medium"
      testId="instance-disable-dialog"
      // SkyDialog closes itself on a primary action, which would throw away a
      // form the server has just refused. The dialog closes when the mutation
      // succeeds, and only then; a failure leaves the typing where it is.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: "Disable instance",
        onClick: () => onConfirm(reason.trim()),
        loading: isSaving,
        destructive: true,
      }}
      secondaryAction={{ label: "Keep active", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        {error ? <ErrorState title="That change was not saved" error={error} /> : null}

        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="Runs in flight finish"
          message="The instance's stored data stays where it is, and so do its runs and its audit trail."
        />

        <SkyMessageBar
          intent="info"
          layout="multiline"
          title="Coming back later"
          // #1812: AI apps are named only while they exist on this deployment.
          message={`Enabling the instance does not switch its schedules back on: someone enables each one.${mcpEnabled ? " Connected AI apps must be approved again." : ""}`}
        />

        <SkyTextarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="Recorded on the audit event beside your name."
          maxLength={500}
          rows={3}
          testId="instance-disable-reason"
        />
      </div>
    </SkyDialog>
  );
}
